import classNames from "classnames";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationPolicyConfigDecision
} from "@idw-types/data/rp-service-configuration";
import { MessagingTemplateCache } from "@idw-types/data/messaging-template";
import { WorkflowChangeRequest } from "@idw-types/data/workflow-change-request";
import { WorkflowServiceDetailsModal } from "@containers/modals/workflow-service-details-modal/workflow-service-details-modal";
import { WorkflowVisualizationPolicyConfigsContainer } from "./workflow-visualization-policy-configs-container/workflow-visualization-policy-configs-container";
import { WorkflowServicePolicyDecisionDetailsModal } from "@containers/modals/workflow-service-policy-decision-details-modal/workflow-service-policy-decision-details-modal";
import styles from "./workflow-visualization-container.less";

type WorkflowVisualizationContainerProps = {
    className?: string;
    rpServices: Array<FormattedRpServiceConfiguration>;
    hiddenServiceApiKeys?: Array<string>;
    primaryRpServiceId: string;
    workflowMessagingTemplateCache: MessagingTemplateCache;
    availableLanguages: Array<LocaleTypes>;
    hasUnsubmittedChangeRequest: boolean;
    workflowChangeRequests: Array<WorkflowChangeRequest>;
    selectedLanguage: LocaleTypes;
    setRpServiceConfigurationsState: (rpServices: Array<FormattedRpServiceConfiguration>) => void;
    setWorkflowMessagingTemplateCache: (workflowMessagingTemplateCache: MessagingTemplateCache) => void;
    onClickArchiveService: (id: string) => void;
    onClickSetAsPrimaryService: (id: string) => void;
    generateStartChangeRequestButton: (
        setWorkflowChangeRequests: (changeRequests: Array<WorkflowChangeRequest>) => void
    ) => React.ReactNode;
};

export const WorkflowVisualizationContainer = (props: WorkflowVisualizationContainerProps) => {
    const {
        className,
        rpServices,
        hiddenServiceApiKeys,
        primaryRpServiceId,
        workflowMessagingTemplateCache,
        selectedLanguage,
        availableLanguages,
        workflowChangeRequests,
        hasUnsubmittedChangeRequest,
        setWorkflowMessagingTemplateCache,
        setRpServiceConfigurationsState,
        onClickArchiveService,
        onClickSetAsPrimaryService,
        generateStartChangeRequestButton
    } = props;

    const modalContext = useModalContext();

    const onClickOpenServiceDetails = (selectedRpService: FormattedRpServiceConfiguration) => {
        const renderWorkflowServiceDetailsModal = (onModalClose: () => void) => {
            return (
                <WorkflowServiceDetailsModal
                    availableLanguages={availableLanguages}
                    initialSelectedLanguage={selectedLanguage}
                    initialSelectedService={selectedRpService}
                    initialRpServiceConfigurations={rpServices}
                    hiddenServiceApiKeys={hiddenServiceApiKeys}
                    initialWorkflowMessagingTemplateCache={workflowMessagingTemplateCache}
                    initialWorkflowChangeRequests={workflowChangeRequests}
                    onModalClose={onModalClose}
                    setRpServiceConfigurationsState={setRpServiceConfigurationsState}
                    setWorkflowMessagingTemplateCache={setWorkflowMessagingTemplateCache}
                    generateStartChangeRequestButton={generateStartChangeRequestButton}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.WorkflowServiceDetails,
            renderFunction: renderWorkflowServiceDetailsModal
        });
    };

    const onClickOpenPolicyDecisionDetails = (
        policyDecisions: Array<FormattedRpServiceConfigurationPolicyConfigDecision>,
        rpService: FormattedRpServiceConfiguration,
        isDefaultAction: boolean
    ) => {
        const renderWorkflowServicePolicyDecisionDetailsModal = (onModalClose: () => void) => {
            return (
                <WorkflowServicePolicyDecisionDetailsModal
                    onModalClose={onModalClose}
                    policyDecisions={policyDecisions}
                    rpService={rpService}
                    hiddenServiceApiKeys={hiddenServiceApiKeys}
                    isDefaultAction={isDefaultAction}
                    hasUnsubmittedChangeRequest={hasUnsubmittedChangeRequest}
                    selectedLanguage={selectedLanguage}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.WorkflowServicePolicyDecisionDetails,
            renderFunction: renderWorkflowServicePolicyDecisionDetailsModal
        });
    };

    return (
        <div className={classNames(styles.workflowVisualizationContainer, className ? className : "")}>
            <WorkflowVisualizationPolicyConfigsContainer
                rpServices={rpServices}
                primaryRpServiceId={primaryRpServiceId}
                hasUnsubmittedChangeRequest={hasUnsubmittedChangeRequest}
                onClickOpenPolicyDecisionDetails={onClickOpenPolicyDecisionDetails}
                onClickOpenServiceDetails={onClickOpenServiceDetails}
                onClickArchiveService={onClickArchiveService}
                onClickSetAsPrimaryService={onClickSetAsPrimaryService}
            />
        </div>
    );
};